import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createGLAccount, updateGLAccount, deleteGLAccount, seedGLAccounts, fetchAllPages } from '../api';
import { formatCurrency } from '../utils/currency';
import { useConfirm } from './ConfirmProvider';
import GLAccountTransactionsPanel from './GLAccountTransactionsPanel';

const CATEGORY_OPTIONS = [
  { value: 'ASSET', label: 'Asset' },
  { value: 'LIABILITY', label: 'Liability' },
  { value: 'EQUITY', label: 'Equity' },
  { value: 'REVENUE', label: 'Revenue' },
  { value: 'EXPENSE', label: 'Expense' },
  { value: 'CONTRA', label: 'Contra' },
  { value: 'COST_OF_SALES', label: 'Cost of Sales' },
];

// Keys MUST match GLAccount.SubCategory in legal_ops/models.py. Mismatches
// were the root cause of QA Bugs #1 (Owner's Equity) and #6 (Administrative)
// in the 2026-05-29 report — the dropdown sent OWNERS_EQUITY / ADMINISTRATIVE
// while the backend enum only accepts OWNER_EQUITY / ADMINISTRATIVE_EXPENSE.
const SUB_CATEGORY_MAP = {
  ASSET: [
    { value: 'CURRENT_ASSET', label: 'Current Asset' },
    { value: 'NON_CURRENT_ASSET', label: 'Non-Current Asset' },
    { value: 'FIXED_ASSETS', label: 'Fixed Assets' },
    { value: 'OTHER_ASSETS', label: 'Other Assets' },
  ],
  LIABILITY: [
    { value: 'CURRENT_LIABILITY', label: 'Current Liability' },
    { value: 'NON_CURRENT_LIABILITY', label: 'Non-Current Liability' },
    { value: 'LONG_TERM_LIABILITY', label: 'Long-Term Liability' },
  ],
  EQUITY: [
    { value: 'OWNER_EQUITY', label: "Owner's Equity" },
    { value: 'RETAINED_EARNINGS', label: 'Retained Earnings' },
  ],
  REVENUE: [
    { value: 'OPERATING_REVENUE', label: 'Operating Revenue' },
    { value: 'OTHER_REVENUE', label: 'Other Revenue' },
  ],
  EXPENSE: [
    { value: 'OPERATING_EXPENSE', label: 'Operating Expense' },
    { value: 'ADMINISTRATIVE_EXPENSE', label: 'Administrative' },
    { value: 'COST_OF_SERVICE', label: 'Cost of Service' },
    { value: 'OTHER_EXPENSE', label: 'Other Expense' },
  ],
  CONTRA: [
    { value: 'CONTRA_ASSET', label: 'Contra Asset' },
    { value: 'CONTRA_LIABILITY', label: 'Contra Liability' },
    { value: 'CONTRA_REVENUE', label: 'Contra Revenue' },
    { value: 'CONTRA_EXPENSE', label: 'Contra Expense' },
  ],
  COST_OF_SALES: [
    { value: 'DIRECT_COSTS', label: 'Direct Costs' },
    { value: 'CONTRACTOR_COSTS', label: 'Contractor Costs' },
    { value: 'OTHER_COSTS_OF_SALES', label: 'Other Costs of Sales' },
  ],
};

function ChartOfAccounts({ authAxios, showFormMessage, userRole }) {
  const confirm = useConfirm();
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [formData, setFormData] = useState({ code: '', name: '', category: 'ASSET', sub_category: 'CURRENT_ASSET', description: '', is_active: true });
  const [ledgerAccount, setLedgerAccount] = useState(null);

  const canManage = useMemo(() => ['PARTNER', 'SUPERUSER', 'ACCOUNTANT'].includes(userRole), [userRole]);

  const fetchAccounts = useCallback(async () => {
    setIsLoading(true);
    try {
      // fetchAllPages walks every backend page. GLAccountViewSet's own
      // ChartOfAccountsPagination override (page_size=500) already kept
      // this safe up to 500 accounts, but a plain single GET here would
      // silently regress to the exact truncation bug that override was
      // built to fix (docs/audits/pagination-audit-2026-08-13.md) the
      // moment any firm's chart of accounts exceeds that.
      const data = await fetchAllPages(authAxios, 'gl-accounts/');
      setAccounts(Array.isArray(data) ? data : []);
    } catch (err) {
      showFormMessage('error', 'Failed to load chart of accounts.');
    } finally {
      setIsLoading(false);
    }
  }, [authAxios, showFormMessage]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const handleSeed = async () => {
    try {
      await seedGLAccounts(authAxios);
      showFormMessage('success', 'Default accounts seeded successfully.');
      fetchAccounts();
    } catch (err) {
      showFormMessage('error', 'Failed to seed accounts.');
    }
  };

  const openNewForm = () => {
    setEditingAccount(null);
    setFormData({ code: '', name: '', category: 'ASSET', sub_category: 'CURRENT_ASSET', description: '', is_active: true });
    setShowForm(true);
  };

  const isTrustAccount = (acct) => acct.code === '1400';

  const openEditForm = (acct) => {
    if (isTrustAccount(acct)) {
      showFormMessage('error', 'Account 1400 (Trust) is system-managed and cannot be edited.');
      return;
    }
    setEditingAccount(acct);
    setFormData({ code: acct.code, name: acct.name, category: acct.category, sub_category: acct.sub_category, description: acct.description || '', is_active: acct.is_active });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAccount) {
        await updateGLAccount(authAxios, editingAccount.id, formData);
        showFormMessage('success', 'Account updated.');
      } else {
        await createGLAccount(authAxios, formData);
        showFormMessage('success', 'Account created.');
      }
      setShowForm(false);
      fetchAccounts();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to save account.');
    }
  };

  const handleDelete = async (id) => {
    const acct = accounts.find(a => a.id === id);
    if (acct && isTrustAccount(acct)) {
      showFormMessage('error', 'Account 1400 (Trust) is system-managed and cannot be deleted.');
      return;
    }
    const ok = await confirm({
      title: 'Delete Account',
      message: 'Are you sure you want to delete this account? This cannot be undone.',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteGLAccount(authAxios, id);
      showFormMessage('success', 'Account deleted.');
      fetchAccounts();
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || 'Cannot delete account with posted journal lines. Consider deactivating it instead.';
      showFormMessage('error', msg);
    }
  };

  const filteredAccounts = useMemo(() => {
    return accounts.filter(a => {
      if (categoryFilter && a.category !== categoryFilter) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return a.code.toLowerCase().includes(term) || a.name.toLowerCase().includes(term);
      }
      return true;
    });
  }, [accounts, categoryFilter, searchTerm]);

  const groupedAccounts = useMemo(() => {
    const groups = {};
    for (const acct of filteredAccounts) {
      const cat = acct.category || 'OTHER';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(acct);
    }
    return groups;
  }, [filteredAccounts]);

  const subCategoryOptions = SUB_CATEGORY_MAP[formData.category] || [];

  return (
    <div className="animate__animated animate__fadeIn">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Chart of Accounts</h2>
          <p className="text-gray-500 text-sm mt-1">Manage your firm's general ledger accounts</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <button onClick={handleSeed} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">
              Seed Defaults
            </button>
            <button onClick={openNewForm} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium">
              + New Account
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input type="text" placeholder="Search by code or name..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">All Categories</option>
          {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold mb-4">{editingAccount ? 'Edit Account' : 'New Account'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <input type="text" required value={formData.code} onChange={e => setFormData({...formData, code: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. 1000" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input type="text" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Bank Account" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select value={formData.category} onChange={e => { const cat = e.target.value; const subs = SUB_CATEGORY_MAP[cat] || []; setFormData({...formData, category: cat, sub_category: subs[0]?.value || ''}); }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    {CATEGORY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sub-Category</label>
                  <select value={formData.sub_category} onChange={e => setFormData({...formData, sub_category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    {subCategoryOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={2} />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} />
                Active
              </label>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">
                  {editingAccount ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Account listing grouped by category */}
      {isLoading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
      ) : accounts.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <p className="text-gray-500 mb-4">No accounts found. Seed default accounts to get started.</p>
          {canManage && <button onClick={handleSeed} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm">Seed Default Accounts</button>}
        </div>
      ) : (
        Object.entries(groupedAccounts).map(([category, accts]) => (
          <div key={category} className="mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
              {CATEGORY_OPTIONS.find(c => c.value === category)?.label || category}
            </h3>
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sub-Category</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ledger</th>
                    {canManage && <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {accts.map(a => (
                    <tr key={a.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-gray-700">
                        {a.code}
                        {isTrustAccount(a) && <span className="ml-2 inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700">TRUST</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-800">{a.name}</td>
                      <td className="px-4 py-3 text-gray-500">{(a.sub_category || '').replace(/_/g, ' ')}</td>
                      <td className="px-4 py-3 text-right font-medium text-gray-800">
                        {a.balance != null ? formatCurrency(a.balance) : '—'}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${a.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {a.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => setLedgerAccount(a)} className="text-blue-600 hover:text-blue-800 text-xs font-medium">
                          View Transactions
                        </button>
                      </td>
                      {canManage && (
                        <td className="px-4 py-3 text-right">
                          {isTrustAccount(a) ? (
                            <span className="text-gray-400 text-xs italic">System-managed</span>
                          ) : (
                            <>
                              <button onClick={() => openEditForm(a)} className="text-blue-600 hover:text-blue-800 text-xs font-medium mr-3">Edit</button>
                              <button onClick={() => handleDelete(a.id)} className="text-red-500 hover:text-red-700 text-xs font-medium">Delete</button>
                            </>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}

      {ledgerAccount && (
        <GLAccountTransactionsPanel
          account={ledgerAccount}
          authAxios={authAxios}
          showFormMessage={showFormMessage}
          onClose={() => setLedgerAccount(null)}
        />
      )}
    </div>
  );
}

export default ChartOfAccounts;
